import { useState } from 'react'

export interface GpsResult {
  lat: number
  lng: number
  accuracyM: number
}

export type GpsErrorKind = 'denied' | 'unavailable' | 'timeout' | 'unsupported'

export class GpsError extends Error {
  constructor(public kind: GpsErrorKind, message: string) {
    super(message)
    this.name = 'GpsError'
  }
}

export type GpsState = 'idle' | 'requesting' | 'success' | 'error'

export function useGeolocation(): {
  state: GpsState
  request: () => Promise<GpsResult>
} {
  const [state, setState] = useState<GpsState>('idle')

  const request = async (): Promise<GpsResult> => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setState('error')
      throw new GpsError('unsupported', 'Przeglądarka nie obsługuje geolokalizacji')
    }

    setState('requesting')

    // Sprawdź permission jeśli API dostępne — pomija prompt jak już denied.
    if (navigator.permissions?.query) {
      try {
        const perm = await navigator.permissions.query({
          name: 'geolocation' as PermissionName,
        })
        if (perm.state === 'denied') {
          setState('error')
          throw new GpsError('denied', 'Lokalizacja zablokowana')
        }
      } catch (err) {
        // Niektóre przeglądarki rzucają na `name: 'geolocation'`. Ignorujemy
        // i lecimy do getCurrentPosition — dostaniemy PERMISSION_DENIED jeśli trzeba.
        if (err instanceof GpsError) throw err
      }
    }

    try {
      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10_000,
          maximumAge: 0,
        })
      })
      setState('success')
      return {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracyM: pos.coords.accuracy,
      }
    } catch (err) {
      setState('error')
      const pe = err as GeolocationPositionError
      if (pe.code === 1) throw new GpsError('denied', 'Lokalizacja zablokowana')
      if (pe.code === 2)
        throw new GpsError('unavailable', 'Nie udało się ustalić lokalizacji')
      if (pe.code === 3) throw new GpsError('timeout', 'Lokalizacja: czas oczekiwania minął')
      throw new GpsError('unavailable', 'Błąd lokalizacji')
    }
  }

  return { state, request }
}
